<template>
  <div>
    <!-- Drawer Subscription Whitelist -->
    <BSidebar
      id="drawer-subscription-whitelist"
      ref="drawer-subscription-whitelist"
      right
      backdrop
      :visible="isVisible"
      width="520px"
      class="drawer-z-index white-drawer"
      @hidden="closeDrawer"
    >
      <div class="justify-center flex flex-column p-2 min-h-full">
        <div class="flex flex-col text-left gap-2 justify-content-center mb-2">
          <strong class="text-black text-xl">{{ cta.title }}</strong>
          <span class="text-md text-[#737373]">
            {{ cta.description }}
          </span>
        </div>

        <ValidationObserver
          ref="observer"
          #default="{ invalid }"
          class="flex-1 flex flex-col"
        >
          <!-- Saldo Kompay -->
          <div class="mb-1 border border-gray-200 rounded-md py-2 ps-4 flex justify-between items-center">
            <div class="flex flex-col">
              <label class="text-md block text-[#737373]">Saldo Kompay</label>
              <div class="text-2xl font-medium text-black">
                {{ IDR(saldo) }}
              </div>
            </div>
          </div>
          <small
            v-if="!hasEnoughSaldo"
            class="flex flex-row items-center gap-2 text-danger my-2"
          >
            <img
              src="https://storage.googleapis.com/komerce/assets/komerce-icon/Orange/danger.svg"
              alt="danger"
              width="16"
              height="16"
            >
            <p class="m-0 text-xs">
              Nominal Saldo Kompay kurang, Silahkan
              <a
                class="!text-[#08A0F7] !underline"
                @click.prevent="
                  $router.push({ name: 'dashboard-komship' })
                "
              >topup saldo kompaymu</a>
            </p>
          </small>

          <!-- Batas Minimal Saldo Ideal -->
          <div
            v-if="showIdealBalance"
            class="bg-[#FFEDED] p-1 rounded-lg my-2"
          >
            <div class="d-flex">
              <span class="k-info-circle-1 text-danger font-bold align-content-center mr-1 text-xl" />
              <div class="font-medium text-black text-[14px] align-content-center">
                Batas Minimal Saldo Ideal :
                <b>
                  {{ IDR(idealBalance) }}
                </b>
              </div>
            </div>
            <div class="mb-1">
              Saldo ideal diterapkan untuk menghindari saldo minus.
              <a
                class="text-[#08A0F7] underline"
                href="https://bantuan.komerce.id/id/article/saldo-ideal-komship-7dn9vp/"
                target="_blank"
              >Pelajari lebih lanjut</a>
            </div>
            <div class="p-1 bg-[#FFFFFF] rounded-lg">
              <div class="flex">
                <span class="k-message-question font-bold align-content-center mr-1 text-lg" />
                <div class="font-medium content-center">
                  Darimana Saldo Ideal berasal?
                </div>
              </div>
              <span>
                Potensi Pendapatan Order Dikirim
                <b>{{ IDR(incomeOrderPotential) }}</b>
                - Ongkir Retur Ketika Sampai
                <b>{{ IDR(potencyRetur) }}</b>
                - Resiko Ongkir Menjadi Retur
                <b>{{ IDR(riskRetur) }}</b>
                = Saldo Ideal
                <b class="text-primary">{{ IDR(idealBalance) }}</b>
              </span>
            </div>
          </div>

          <!-- Nominal Top Up Awal -->
          <BFormGroup
            v-show="isActivation"
            label-for="nominal"
          >
            <template #label>
              <span class="text-base text-[#737373] mt-2">Nominal Top Up Awal</span>
              <span class="text-base text-red-500">*</span>
            </template>
            <ValidationProvider
              v-slot="{ errors }"
              name="Top Up"
              :rules="isActivation ? {
                required: 'Nominal harus diisi',
                min_currency: MIN_BALANCE,
              } : ''"
              label-for="nominal"
              label-slot="label-nominal"
            >
              <BFormInput
                v-model="nominal"
                type="text"
                maxlength="11"
                placeholder="Masukkan nominal disini"
                @keypress="isNumber($event)"
                @input="formatNominal"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </ValidationProvider>

            <div
              :class="
                `grid grid-cols-${
                  nominalOptions.length % 2 === 0 ? 2 : 1
                } md:grid-cols-${nominalOptions.length} gap-2 mt-1`
              "
            >
              <BButton
                v-for="(amount, index) in nominalOptions"
                :key="index"
                :variant="
                  selectedNominal === amount ? 'primary' : 'outline-secondary'
                "
                class="rounded-lg p-1"
                @click="selectNominal(amount)"
              >
                {{ IDR(amount) }}
              </BButton>
            </div>
          </BFormGroup>

          <!-- Durasi Langganan -->
          <BFormGroup>
            <template #label>
              <span class="text-base text-[#737373] mt-2">Durasi Langganan</span>
              <span class="text-base text-red-500">*</span>
            </template>
            <div class="grid grid-cols-3 gap-2">
              <BButton
                v-for="subs in subscriptionOptions"
                :key="subs.id"
                :variant="selectedDuration.duration_day === subs.duration_day ? 'outline-primary' : 'outline-secondary'"
                class="rounded-lg px-2 py-1 w-full bg-white"
                @click="selectDuration(subs)"
              >
                <div class="flex flex-col text-left items-center gap-0.5">
                  <span class="text-sm">Durasi {{ subs.duration_day }} Hari</span>
                  <strong>{{ IDR(subs.price) }}</strong>
                </div>
              </BButton>
            </div>
          </BFormGroup>

          <!-- Top Up Details -->
          <div v-if="isActivation && !(invalid || isSubmitDisabled)">
            <div class="flex justify-between text-black mb-[.5rem]">
              <div>Nominal top up</div>
              <div>{{ IDR(getNominalValue) }}</div>
            </div>
            <div class="flex justify-between text-black mb-[.25rem]">
              <div>Top up fee ({{ feeTopup }} %)</div>
            </div>
            <div class="flex justify-between text-black mb-[.25rem] pl-1">
              <div>• Fee</div>
              <div>{{ IDR(getFee) }}</div>
            </div>
            <div class="flex justify-between text-black mb-[.5rem] pl-1">
              <div>• PPN</div>
              <div>{{ IDR(getPPN) }}</div>
            </div>
            <div class="flex justify-between text-black mb-[.5rem]">
              <div>Durasi Berlangganan ({{ selectedDuration.duration_day }} Hari)</div>
              <div>{{ IDR(selectedDuration.price) }}</div>
            </div>
          </div>

          <!-- Total Pembayaran -->
          <div class="mt-2">
            <div class="flex justify-between font-medium text-black">
              <div class="flex items-center">
                Total Pembayaran
                <img
                  id="totalPaymentInfo"
                  src="https://storage.googleapis.com/komerce/assets/icons/info-circle.svg"
                  alt="info"
                  width="16"
                  height="16"
                  class="ml-1"
                >
                <b-popover
                  target="totalPaymentInfo"
                  triggers="hover"
                  placement="top"
                  custom-class="!bg-[#333333]"
                >
                  <h1 class="text-base font-semibold text-white">
                    Total Pembayaran
                  </h1>
                  <span
                    class="text-white"
                  >Nominal top Up yang akan kamu bayarkan</span>
                </b-popover>
              </div>
              <div class="text-xl font-medium">
                {{ IDR(totalPayment) }}
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="mt-auto flex cols cols-12 gap-2">
            <BButton
              variant="outline-primary"
              class="rounded-lg px-4 w-full cols-6"
              @click="closeDrawer"
            >
              Batal
            </BButton>
            <BButton
              :variant="invalid || isSubmitDisabled ? 'secondary' : 'primary'"
              class="rounded-lg px-4 bg-[#FF5C00] border-[#FF5C00] w-full cols-6"
              :disabled="invalid || isSubmitDisabled || showIdealBalance"
              @click="onConfirm"
            >
              {{ isActivation ? 'Top Up Sekarang' : 'Berlangganan' }}
            </BButton>
          </div>
        </ValidationObserver>
      </div>
    </BSidebar>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required, minCurrency } from '@validations'
import { IDR } from '@/libs/currency'
import { mapState } from 'vuex'
import { newAxiosIns } from '@/libs/axios'
import { isNumber } from '@/libs/helpers'
import moment from 'moment'

export default {
  name: 'DrawerSubscriptionWhitelist',
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    whitelistStatus: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      required,
      minCurrency,
      IDR,
      isNumber,
      nominal: '',
      BASE_MIN_BALANCE: 300000,
      BASE_NOMINAL_OPTIONS: [300000, 500000, 1000000, 1500000],
      selectedNominal: null,
      subscriptionOptions: null,
      selectedDuration: {},
      allPlatformOptions: [
        { name: 'Google', image: 'https://storage.googleapis.com/komerce/komads-meta/google.svg', value: 'google' },
      ],
      // Ideal balance analytics
      idealBalance: 0,
      incomeOrderPotential: 0,
      potencyRetur: 0,
      riskRetur: 0,
      loading: false,
      whitelistInfo: null,
      totalSpendPlatform: [],
      feeTopup: 7,
      tax: 0.11,
      dppTax: 1.11,
    }
  },
  computed: {
    ...mapState('dashboard', ['profile']),
    ...mapState('saldo', ['saldo']),
    isActivation() {
      return this.whitelistStatus === 'new'
    },
    cta() {
      if (this.isActivation) return { title: 'Top Up Aktivasi Akun', description: 'Lakukan top up saldo awal dan biaya sewa untuk mengaktifkan akun baru kamu.' }
      return { title: 'Perpanjang Akun Whitelisted', description: 'Yuk, perpanjang langganan Google Whitelisted agar akun whitelisted-mu tetap aktif di Komads.' }
    },
    totalPayment() {
      return this.selectedDuration.price ? (this.getNominalValue + this.getFee + this.getPPN + this.selectedDuration.price) : 0
    },
    MIN_BALANCE() {
      return this.BASE_MIN_BALANCE
    },
    nominalOptions() {
      return this.BASE_NOMINAL_OPTIONS
    },
    getNominalValue() {
      return Number(this.nominal.replace(/\D/g, ''))
    },
    getPPN() {
      // Calculate PPN based on tax rate, round to nearest integer
      const amountTax = Math.round(this.getNominalValue * (this.feeTopup / 100))
      const dppFee = amountTax / this.dppTax

      // Multiply PPN with tax rate, round to nearest integer
      return Math.round(dppFee * this.tax)
    },
    getFee() {
      // Calculate fee based on tax rate, round to nearest integer
      const amountFee = Math.round(this.getNominalValue * (this.feeTopup / 100))

      // Subtract PPN from fee, round to nearest integer
      return amountFee - this.getPPN
    },
    hasEnoughSaldo() {
      return this.saldo >= this.totalPayment
    },
    showIdealBalance() {
      return (
        this.selectedDuration.price
        && this.hasEnoughSaldo
        && this.profile?.is_komship === 1
        && this.idealBalance > 0
        && this.saldo - this.totalPayment < this.idealBalance
      )
    },
    isSubmitDisabled() {
      return (
        !this.selectedDuration.price
        || this.loading
        || !this.hasEnoughSaldo
        || (this.isActivation && !this.getNominalValue)
      )
    },
    /**
     * Filter platform options to show only platforms with 'accepted' account_request_status
     * and exclude meta platform
     * @returns {Array} Array of platform options that have accepted status
     */
    platformOptions() {
      if (!this.totalSpendPlatform || this.totalSpendPlatform.length === 0) {
        // Fallback to all platforms except meta if no total_spend_platform data is provided
        return this.allPlatformOptions.filter(option => option.name.toLowerCase() !== 'meta')
      }

      // Filter platforms that have 'accepted' status and exclude meta
      const acceptedPlatforms = this.totalSpendPlatform.filter(
        platform => platform.account_request_status === 'accepted'
          && platform.platform_name.toLowerCase() !== 'meta',
      )

      // Map accepted platforms to platform options format
      return this.allPlatformOptions.filter(option => acceptedPlatforms.some(accepted => accepted.platform_name.toLowerCase() === option.name.toLowerCase()))
    },
  },
  watch: {
    isVisible(val) {
      if (val) {
        this.getSubscriptionFee()
        this.getWhitelistInfo()
        this.checkIdealBalance()
        this.$store.dispatch('saldo/getBalanceSummary')
      } else {
        this.nominal = ''
        this.selectedNominal = null
      }
    },
    platform() {
      if (this.isVisible) {
        this.getSubscriptionFee()
      }
    },
  },
  async mounted() {
    await this.getWhitelistInfo()
    await this.checkIdealBalance()
    if (this.isVisible) {
      await this.getSubscriptionFee()
      this.$store.dispatch('saldo/getBalanceSummary')
    }
  },
  methods: {
    /**
     * Handle confirm berlangganan
     */
    onConfirm() {
      const form = { duration: this.selectedDuration, nominal: this.getNominalValue }
      if (this.whitelistStatus === 'new') {
        form.nominalFee = this.getFee
        form.nominalTax = this.getPPN
      }
      this.$emit('on-confirm', form)
    },
    /**
     * Select duration for subscription
     * @param {Object} subs - Subscription option object
     */
    selectDuration(subs) {
      this.selectedDuration = subs
    },
    formatNominal() {
      const value = this.nominal.replace(/\D/g, '')
      this.nominal = value ? value.replace(/\B(?=(\d{3})+(?!\d))/g, '.') : ''

      // Check if the current value matches any of the nominal options
      const currentValue = parseInt(value, 10)
      const matchingOption = this.nominalOptions.find(
        option => option === currentValue,
      )
      this.selectedNominal = matchingOption || null
    },
    selectNominal(amount) {
      this.selectedNominal = amount
      this.nominal = amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    closeDrawer() {
      this.selectedDuration = {}
      this.nominal = ''
      this.selectedNominal = null
      this.$nextTick(() => {
        if (this.$refs.observer) {
          this.$refs.observer.reset()
        }
      })
      this.$emit('onVisible', false)
    },
    /**
     * Get whitelist info for the current month from the API
     *
     * @returns {void}
     * @throws {Error}
     */
    async getWhitelistInfo() {
      try {
        const response = await newAxiosIns.get('/komads/api/v1/partners/whitelist_info', {
          params: {
            month: moment().format('MM-YYYY'),
          },
        })
        const { data } = response.data
        this.whitelistInfo = data
        this.totalSpendPlatform = data.total_spend_platform
      } catch (error) {
        console.warn(error)
      }
    },
    /**
     * Fetch ideal balance analytics from API
     *
     * @private
     * @returns {void}
     */
    async checkIdealBalance() {
      if (this.profile && this.profile.is_komship === 1) {
        try {
          const response = await newAxiosIns.get(`/komship/api/v1/admin/partner-consumables/balance-analitycs/${this.profile.partner_id}`)
          const { data } = response.data
          this.idealBalance = data.ideal_balance
          this.incomeOrderPotential = data.income_order_potential
          this.potencyRetur = data.ongkir_retur_on_finished
          this.riskRetur = data.ongkir_risk_become_retur
        } catch (e) {
          console.error(e)
        }
      }
    },
    async getSubscriptionFee() {
      try {
        const { data } = await newAxiosIns.get('/komads/api/v1/subscription_fees', {
          params: {
            platform_name: 'google',
          },
        })

        this.subscriptionOptions = data.data
      } catch (error) {
        this.$toast_error({ message: 'Gagal mengambil data fee' })
      }
    },
  },
}
</script>

<style lang="css" scoped>
.border {
  border-color: #e2e2e2 !important;
}

.drawer-z-index {
  z-index: 9999999 !important;
}

::v-deep .white-drawer .b-sidebar-header,
::v-deep .white-drawer .b-sidebar-body {
  background-color: white;
}
</style>
